import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import {
  parseConfigH,
  generateConfigH,
  ConfigDefines,
} from '../../../services/workbench/ConfigHParser';

// Section header component (same pattern as KeyboardJsonEditorDialog)
function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 'bold',
        color: 'text.secondary',
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.08em',
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

// Switch with description helper text
function SwitchField({
  label,
  checked,
  onChange,
  helperText,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  helperText: string;
}) {
  return (
    <div>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        label={label}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', ml: '42px', mt: -0.5 }}
      >
        {helperText}
      </Typography>
    </div>
  );
}

type ConfigHSettingsPanelProps = {
  configHContent: string | null;
  onChange?: (updatedContent: string) => void;
};

export function ConfigHSettingsPanel(props: ConfigHSettingsPanelProps) {
  // Track code generated by this panel to avoid re-initialization loops
  const lastGeneratedCodeRef = useRef<string | null>(null);

  // Synchronously check whether the config.h content is parseable
  const isConfigValid = useMemo(() => {
    if (!props.configHContent) return false;
    if (props.configHContent === lastGeneratedCodeRef.current) return true;
    try {
      parseConfigH(props.configHContent);
      return true;
    } catch {
      return false;
    }
  }, [props.configHContent]);

  // --- Tap/Hold ---
  const [tappingTerm, setTappingTerm] = useState('');
  const [tappingTermPerKey, setTappingTermPerKey] = useState(false);
  const [permissiveHold, setPermissiveHold] = useState(false);
  const [permissiveHoldPerKey, setPermissiveHoldPerKey] = useState(false);
  const [holdOnOtherKeyPress, setHoldOnOtherKeyPress] = useState(false);
  const [holdOnOtherKeyPressPerKey, setHoldOnOtherKeyPressPerKey] =
    useState(false);
  const [quickTapTerm, setQuickTapTerm] = useState('');
  const [quickTapTermPerKey, setQuickTapTermPerKey] = useState(false);
  const [retroTapping, setRetroTapping] = useState(false);
  const [retroTappingPerKey, setRetroTappingPerKey] = useState(false);

  // --- Mouse Keys ---
  const [mousekeyDelay, setMousekeyDelay] = useState('');
  const [mousekeyInterval, setMousekeyInterval] = useState('');
  const [mousekeyMaxSpeed, setMousekeyMaxSpeed] = useState('');
  const [mousekeyTimeToMax, setMousekeyTimeToMax] = useState('');
  const [mousekeyWheelDelay, setMousekeyWheelDelay] = useState('');
  const [mousekeyWheelInterval, setMousekeyWheelInterval] = useState('');
  const [mousekeyWheelMaxSpeed, setMousekeyWheelMaxSpeed] = useState('');
  const [mousekeyWheelTimeToMax, setMousekeyWheelTimeToMax] = useState('');

  // --- Combo ---
  const [comboTerm, setComboTerm] = useState('');
  const [comboMustHoldMods, setComboMustHoldMods] = useState(false);
  const [comboHoldTerm, setComboHoldTerm] = useState('');
  const [comboOnlyFromLayer, setComboOnlyFromLayer] = useState('');

  // --- Auto Shift ---
  const [autoShiftTimeout, setAutoShiftTimeout] = useState('');
  const [autoShiftModifiers, setAutoShiftModifiers] = useState(false);
  const [noAutoShiftSpecial, setNoAutoShiftSpecial] = useState(false);
  const [noAutoShiftNumeric, setNoAutoShiftNumeric] = useState(false);
  const [noAutoShiftAlpha, setNoAutoShiftAlpha] = useState(false);

  // --- One Shot ---
  const [oneshotTimeout, setOneshotTimeout] = useState('');
  const [oneshotTapToggle, setOneshotTapToggle] = useState('');

  // --- Leader Key ---
  const [leaderTimeout, setLeaderTimeout] = useState('');
  const [leaderPerKeyTiming, setLeaderPerKeyTiming] = useState(false);
  const [leaderNoTimeout, setLeaderNoTimeout] = useState(false);

  // --- Hardware ---
  const [usbVbusPin, setUsbVbusPin] = useState('');

  // --- Audio ---
  const [audioPin, setAudioPin] = useState('');
  const [audioPinAlt, setAudioPinAlt] = useState('');
  const [audioVoices, setAudioVoices] = useState(false);
  const [audioInitDelay, setAudioInitDelay] = useState(false);

  // Helper to read a numeric value from defines
  const getNumeric = (defines: ConfigDefines, key: string): string => {
    const val = defines.get(key);
    return typeof val === 'string' ? val : '';
  };

  // Helper to read a boolean flag from defines
  const getFlag = (defines: ConfigDefines, key: string): boolean => {
    return defines.has(key);
  };

  // Initialize form from config.h content
  useEffect(() => {
    if (!props.configHContent) return;
    if (props.configHContent === lastGeneratedCodeRef.current) return;
    try {
      const defines = parseConfigH(props.configHContent);

      // Tap/Hold
      setTappingTerm(getNumeric(defines, 'TAPPING_TERM'));
      setTappingTermPerKey(getFlag(defines, 'TAPPING_TERM_PER_KEY'));
      setPermissiveHold(getFlag(defines, 'PERMISSIVE_HOLD'));
      setPermissiveHoldPerKey(getFlag(defines, 'PERMISSIVE_HOLD_PER_KEY'));
      setHoldOnOtherKeyPress(getFlag(defines, 'HOLD_ON_OTHER_KEY_PRESS'));
      setHoldOnOtherKeyPressPerKey(
        getFlag(defines, 'HOLD_ON_OTHER_KEY_PRESS_PER_KEY')
      );
      setQuickTapTerm(getNumeric(defines, 'QUICK_TAP_TERM'));
      setQuickTapTermPerKey(getFlag(defines, 'QUICK_TAP_TERM_PER_KEY'));
      setRetroTapping(getFlag(defines, 'RETRO_TAPPING'));
      setRetroTappingPerKey(getFlag(defines, 'RETRO_TAPPING_PER_KEY'));

      // Mouse Keys
      setMousekeyDelay(getNumeric(defines, 'MOUSEKEY_DELAY'));
      setMousekeyInterval(getNumeric(defines, 'MOUSEKEY_INTERVAL'));
      setMousekeyMaxSpeed(getNumeric(defines, 'MOUSEKEY_MAX_SPEED'));
      setMousekeyTimeToMax(getNumeric(defines, 'MOUSEKEY_TIME_TO_MAX'));
      setMousekeyWheelDelay(getNumeric(defines, 'MOUSEKEY_WHEEL_DELAY'));
      setMousekeyWheelInterval(getNumeric(defines, 'MOUSEKEY_WHEEL_INTERVAL'));
      setMousekeyWheelMaxSpeed(getNumeric(defines, 'MOUSEKEY_WHEEL_MAX_SPEED'));
      setMousekeyWheelTimeToMax(
        getNumeric(defines, 'MOUSEKEY_WHEEL_TIME_TO_MAX')
      );

      // Combo
      setComboTerm(getNumeric(defines, 'COMBO_TERM'));
      setComboMustHoldMods(getFlag(defines, 'COMBO_MUST_HOLD_MODS'));
      setComboHoldTerm(getNumeric(defines, 'COMBO_HOLD_TERM'));
      setComboOnlyFromLayer(getNumeric(defines, 'COMBO_ONLY_FROM_LAYER'));

      // Auto Shift
      setAutoShiftTimeout(getNumeric(defines, 'AUTO_SHIFT_TIMEOUT'));
      setAutoShiftModifiers(getFlag(defines, 'AUTO_SHIFT_MODIFIERS'));
      setNoAutoShiftSpecial(getFlag(defines, 'NO_AUTO_SHIFT_SPECIAL'));
      setNoAutoShiftNumeric(getFlag(defines, 'NO_AUTO_SHIFT_NUMERIC'));
      setNoAutoShiftAlpha(getFlag(defines, 'NO_AUTO_SHIFT_ALPHA'));

      // One Shot
      setOneshotTimeout(getNumeric(defines, 'ONESHOT_TIMEOUT'));
      setOneshotTapToggle(getNumeric(defines, 'ONESHOT_TAP_TOGGLE'));

      // Leader Key
      setLeaderTimeout(getNumeric(defines, 'LEADER_TIMEOUT'));
      setLeaderPerKeyTiming(getFlag(defines, 'LEADER_PER_KEY_TIMING'));
      setLeaderNoTimeout(getFlag(defines, 'LEADER_NO_TIMEOUT'));

      // Hardware
      setUsbVbusPin(getNumeric(defines, 'USB_VBUS_PIN'));

      // Audio
      setAudioPin(getNumeric(defines, 'AUDIO_PIN'));
      setAudioPinAlt(getNumeric(defines, 'AUDIO_PIN_ALT'));
      setAudioVoices(getFlag(defines, 'AUDIO_VOICES'));
      setAudioInitDelay(getFlag(defines, 'AUDIO_INIT_DELAY'));
    } catch {
      // Invalid config.h - leave defaults
    }
  }, [props.configHContent]);

  // Build config.h from form state
  const buildConfigH = useCallback((): string | null => {
    if (!props.configHContent) return null;
    try {
      const defines: ConfigDefines = new Map();
      const removedKeys = new Set<string>();

      // Helper to set numeric define
      const setNum = (key: string, value: string) => {
        if (value.trim() && !isNaN(Number(value))) {
          defines.set(key, value.trim());
        } else {
          removedKeys.add(key);
        }
      };

      // Helper to set boolean flag
      const setBool = (key: string, value: boolean) => {
        if (value) {
          defines.set(key, true);
        } else {
          removedKeys.add(key);
        }
      };

      // Tap/Hold
      setNum('TAPPING_TERM', tappingTerm);
      setBool('TAPPING_TERM_PER_KEY', tappingTermPerKey);
      setBool('PERMISSIVE_HOLD', permissiveHold);
      setBool('PERMISSIVE_HOLD_PER_KEY', permissiveHoldPerKey);
      setBool('HOLD_ON_OTHER_KEY_PRESS', holdOnOtherKeyPress);
      setBool('HOLD_ON_OTHER_KEY_PRESS_PER_KEY', holdOnOtherKeyPressPerKey);
      setNum('QUICK_TAP_TERM', quickTapTerm);
      setBool('QUICK_TAP_TERM_PER_KEY', quickTapTermPerKey);
      setBool('RETRO_TAPPING', retroTapping);
      setBool('RETRO_TAPPING_PER_KEY', retroTappingPerKey);

      // Mouse Keys
      setNum('MOUSEKEY_DELAY', mousekeyDelay);
      setNum('MOUSEKEY_INTERVAL', mousekeyInterval);
      setNum('MOUSEKEY_MAX_SPEED', mousekeyMaxSpeed);
      setNum('MOUSEKEY_TIME_TO_MAX', mousekeyTimeToMax);
      setNum('MOUSEKEY_WHEEL_DELAY', mousekeyWheelDelay);
      setNum('MOUSEKEY_WHEEL_INTERVAL', mousekeyWheelInterval);
      setNum('MOUSEKEY_WHEEL_MAX_SPEED', mousekeyWheelMaxSpeed);
      setNum('MOUSEKEY_WHEEL_TIME_TO_MAX', mousekeyWheelTimeToMax);

      // Combo
      setNum('COMBO_TERM', comboTerm);
      setBool('COMBO_MUST_HOLD_MODS', comboMustHoldMods);
      setNum('COMBO_HOLD_TERM', comboHoldTerm);
      setNum('COMBO_ONLY_FROM_LAYER', comboOnlyFromLayer);

      // Auto Shift
      setNum('AUTO_SHIFT_TIMEOUT', autoShiftTimeout);
      setBool('AUTO_SHIFT_MODIFIERS', autoShiftModifiers);
      setBool('NO_AUTO_SHIFT_SPECIAL', noAutoShiftSpecial);
      setBool('NO_AUTO_SHIFT_NUMERIC', noAutoShiftNumeric);
      setBool('NO_AUTO_SHIFT_ALPHA', noAutoShiftAlpha);

      // One Shot
      setNum('ONESHOT_TIMEOUT', oneshotTimeout);
      setNum('ONESHOT_TAP_TOGGLE', oneshotTapToggle);

      // Leader Key
      setNum('LEADER_TIMEOUT', leaderTimeout);
      setBool('LEADER_PER_KEY_TIMING', leaderPerKeyTiming);
      setBool('LEADER_NO_TIMEOUT', leaderNoTimeout);

      // Hardware
      const setStr = (key: string, value: string) => {
        if (value.trim()) {
          defines.set(key, value.trim());
        } else {
          removedKeys.add(key);
        }
      };
      setStr('USB_VBUS_PIN', usbVbusPin);

      // Audio
      setStr('AUDIO_PIN', audioPin);
      setStr('AUDIO_PIN_ALT', audioPinAlt);
      setBool('AUDIO_VOICES', audioVoices);
      setBool('AUDIO_INIT_DELAY', audioInitDelay);

      return generateConfigH(props.configHContent, defines, removedKeys);
    } catch {
      return null;
    }
  }, [
    props.configHContent,
    tappingTerm,
    tappingTermPerKey,
    permissiveHold,
    permissiveHoldPerKey,
    holdOnOtherKeyPress,
    holdOnOtherKeyPressPerKey,
    quickTapTerm,
    quickTapTermPerKey,
    retroTapping,
    retroTappingPerKey,
    mousekeyDelay,
    mousekeyInterval,
    mousekeyMaxSpeed,
    mousekeyTimeToMax,
    mousekeyWheelDelay,
    mousekeyWheelInterval,
    mousekeyWheelMaxSpeed,
    mousekeyWheelTimeToMax,
    comboTerm,
    comboMustHoldMods,
    comboHoldTerm,
    comboOnlyFromLayer,
    autoShiftTimeout,
    autoShiftModifiers,
    noAutoShiftSpecial,
    noAutoShiftNumeric,
    noAutoShiftAlpha,
    oneshotTimeout,
    oneshotTapToggle,
    leaderTimeout,
    leaderPerKeyTiming,
    leaderNoTimeout,
    usbVbusPin,
    audioPin,
    audioPinAlt,
    audioVoices,
    audioInitDelay,
  ]);

  // Auto-apply form changes with debounce
  useEffect(() => {
    if (!props.onChange || !isConfigValid) return;
    const timer = setTimeout(() => {
      const updated = buildConfigH();
      if (updated !== null && updated !== props.configHContent) {
        lastGeneratedCodeRef.current = updated;
        props.onChange?.(updated);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [buildConfigH, isConfigValid]);

  const [activeTab, setActiveTab] = useState(0);

  if (!isConfigValid) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#666',
          gap: 8,
          padding: 24,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2">
          {t('Could not parse config.h.')}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {t('Please use the Code Editor to fix syntax errors.')}
        </Typography>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          orientation="vertical"
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            flexShrink: 0,
            minWidth: 120,
          }}
        >
          <Tab
            label={t('Tap/Hold')}
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              alignItems: 'flex-start',
            }}
          />
          <Tab
            label={t('Mouse Keys')}
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              alignItems: 'flex-start',
            }}
          />
          <Tab
            label={t('Combo')}
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              alignItems: 'flex-start',
            }}
          />
          <Tab
            label={t('Auto Shift')}
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              alignItems: 'flex-start',
            }}
          />
          <Tab
            label={t('One Shot')}
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              alignItems: 'flex-start',
            }}
          />
          <Tab
            label={t('Leader Key')}
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              alignItems: 'flex-start',
            }}
          />
          <Tab
            label={t('Hardware')}
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              alignItems: 'flex-start',
            }}
          />
          <Tab
            label={t('Audio')}
            sx={{
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              alignItems: 'flex-start',
            }}
          />
        </Tabs>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {/* === Tap/Hold Tab === */}
          {activeTab === 0 && (
            <Stack spacing={3}>
              <div>
                <SectionHeader>{t('Tap/Hold')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="TAPPING_TERM"
                    size="small"
                    type="number"
                    fullWidth
                    value={tappingTerm}
                    onChange={(e) => setTappingTerm(e.target.value)}
                    placeholder="200"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time in ms to distinguish tap from hold (default: 200)'
                    )}
                  />
                  <SwitchField
                    label="TAPPING_TERM_PER_KEY"
                    checked={tappingTermPerKey}
                    onChange={setTappingTermPerKey}
                    helperText={t('Enable per-key tapping term via callback')}
                  />
                  <SwitchField
                    label="PERMISSIVE_HOLD"
                    checked={permissiveHold}
                    onChange={setPermissiveHold}
                    helperText={t(
                      'Trigger hold if another key is pressed and released while held'
                    )}
                  />
                  <SwitchField
                    label="PERMISSIVE_HOLD_PER_KEY"
                    checked={permissiveHoldPerKey}
                    onChange={setPermissiveHoldPerKey}
                    helperText={t(
                      'Enable per-key permissive hold via callback'
                    )}
                  />
                  <SwitchField
                    label="HOLD_ON_OTHER_KEY_PRESS"
                    checked={holdOnOtherKeyPress}
                    onChange={setHoldOnOtherKeyPress}
                    helperText={t(
                      'Trigger hold immediately when another key is pressed'
                    )}
                  />
                  <SwitchField
                    label="HOLD_ON_OTHER_KEY_PRESS_PER_KEY"
                    checked={holdOnOtherKeyPressPerKey}
                    onChange={setHoldOnOtherKeyPressPerKey}
                    helperText={t(
                      'Enable per-key hold on other key press via callback'
                    )}
                  />
                  <TextField
                    label="QUICK_TAP_TERM"
                    size="small"
                    type="number"
                    fullWidth
                    value={quickTapTerm}
                    onChange={(e) => setQuickTapTerm(e.target.value)}
                    placeholder="120"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time in ms for quick tap after tap-hold (default: TAPPING_TERM)'
                    )}
                  />
                  <SwitchField
                    label="QUICK_TAP_TERM_PER_KEY"
                    checked={quickTapTermPerKey}
                    onChange={setQuickTapTermPerKey}
                    helperText={t('Enable per-key quick tap term via callback')}
                  />
                  <SwitchField
                    label="RETRO_TAPPING"
                    checked={retroTapping}
                    onChange={setRetroTapping}
                    helperText={t(
                      'Send tap keycode if no other key was pressed during hold'
                    )}
                  />
                  <SwitchField
                    label="RETRO_TAPPING_PER_KEY"
                    checked={retroTappingPerKey}
                    onChange={setRetroTappingPerKey}
                    helperText={t('Enable per-key retro tapping via callback')}
                  />
                </Stack>
              </div>
            </Stack>
          )}

          {/* === Mouse Keys Tab === */}
          {activeTab === 1 && (
            <Stack spacing={3}>
              <div>
                <SectionHeader>{t('Mouse Keys')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="MOUSEKEY_DELAY"
                    size="small"
                    type="number"
                    fullWidth
                    value={mousekeyDelay}
                    onChange={(e) => setMousekeyDelay(e.target.value)}
                    placeholder="10"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Delay before mouse movement starts in ms (default: 10)'
                    )}
                  />
                  <TextField
                    label="MOUSEKEY_INTERVAL"
                    size="small"
                    type="number"
                    fullWidth
                    value={mousekeyInterval}
                    onChange={(e) => setMousekeyInterval(e.target.value)}
                    placeholder="20"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time between mouse movement steps in ms (default: 20)'
                    )}
                  />
                  <TextField
                    label="MOUSEKEY_MAX_SPEED"
                    size="small"
                    type="number"
                    fullWidth
                    value={mousekeyMaxSpeed}
                    onChange={(e) => setMousekeyMaxSpeed(e.target.value)}
                    placeholder="10"
                    inputProps={{ min: 0 }}
                    helperText={t('Maximum cursor speed (default: 10)')}
                  />
                  <TextField
                    label="MOUSEKEY_TIME_TO_MAX"
                    size="small"
                    type="number"
                    fullWidth
                    value={mousekeyTimeToMax}
                    onChange={(e) => setMousekeyTimeToMax(e.target.value)}
                    placeholder="30"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time to reach max speed in steps (default: 30)'
                    )}
                  />
                </Stack>
              </div>
              <div>
                <SectionHeader>{t('Wheel')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="MOUSEKEY_WHEEL_DELAY"
                    size="small"
                    type="number"
                    fullWidth
                    value={mousekeyWheelDelay}
                    onChange={(e) => setMousekeyWheelDelay(e.target.value)}
                    placeholder="10"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Delay before scroll starts in ms (default: 10)'
                    )}
                  />
                  <TextField
                    label="MOUSEKEY_WHEEL_INTERVAL"
                    size="small"
                    type="number"
                    fullWidth
                    value={mousekeyWheelInterval}
                    onChange={(e) => setMousekeyWheelInterval(e.target.value)}
                    placeholder="80"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time between scroll steps in ms (default: 80)'
                    )}
                  />
                  <TextField
                    label="MOUSEKEY_WHEEL_MAX_SPEED"
                    size="small"
                    type="number"
                    fullWidth
                    value={mousekeyWheelMaxSpeed}
                    onChange={(e) => setMousekeyWheelMaxSpeed(e.target.value)}
                    placeholder="8"
                    inputProps={{ min: 0 }}
                    helperText={t('Maximum scroll speed (default: 8)')}
                  />
                  <TextField
                    label="MOUSEKEY_WHEEL_TIME_TO_MAX"
                    size="small"
                    type="number"
                    fullWidth
                    value={mousekeyWheelTimeToMax}
                    onChange={(e) => setMousekeyWheelTimeToMax(e.target.value)}
                    placeholder="40"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time to reach max scroll speed in steps (default: 40)'
                    )}
                  />
                </Stack>
              </div>
            </Stack>
          )}

          {/* === Combo Tab === */}
          {activeTab === 2 && (
            <Stack spacing={3}>
              <div>
                <SectionHeader>{t('Combo')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="COMBO_TERM"
                    size="small"
                    type="number"
                    fullWidth
                    value={comboTerm}
                    onChange={(e) => setComboTerm(e.target.value)}
                    placeholder="50"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time in ms for combo key detection (default: 50)'
                    )}
                  />
                  <SwitchField
                    label="COMBO_MUST_HOLD_MODS"
                    checked={comboMustHoldMods}
                    onChange={setComboMustHoldMods}
                    helperText={t(
                      'Require modifier combos to be held to activate'
                    )}
                  />
                  <TextField
                    label="COMBO_HOLD_TERM"
                    size="small"
                    type="number"
                    fullWidth
                    value={comboHoldTerm}
                    onChange={(e) => setComboHoldTerm(e.target.value)}
                    placeholder="150"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time in ms to hold combo for hold behavior (default: 150)'
                    )}
                  />
                  <TextField
                    label="COMBO_ONLY_FROM_LAYER"
                    size="small"
                    type="number"
                    fullWidth
                    value={comboOnlyFromLayer}
                    onChange={(e) => setComboOnlyFromLayer(e.target.value)}
                    placeholder="0"
                    inputProps={{ min: 0 }}
                    helperText={t('Only trigger combos from this layer number')}
                  />
                </Stack>
              </div>
            </Stack>
          )}

          {/* === Auto Shift Tab === */}
          {activeTab === 3 && (
            <Stack spacing={3}>
              <div>
                <SectionHeader>{t('Auto Shift')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="AUTO_SHIFT_TIMEOUT"
                    size="small"
                    type="number"
                    fullWidth
                    value={autoShiftTimeout}
                    onChange={(e) => setAutoShiftTimeout(e.target.value)}
                    placeholder="175"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time in ms for auto shift activation (default: 175)'
                    )}
                  />
                  <SwitchField
                    label="AUTO_SHIFT_MODIFIERS"
                    checked={autoShiftModifiers}
                    onChange={setAutoShiftModifiers}
                    helperText={t(
                      'Apply auto shift even when modifiers are held'
                    )}
                  />
                  <SwitchField
                    label="NO_AUTO_SHIFT_SPECIAL"
                    checked={noAutoShiftSpecial}
                    onChange={setNoAutoShiftSpecial}
                    helperText={t(
                      'Disable auto shift for special keys (e.g. -=[] etc.)'
                    )}
                  />
                  <SwitchField
                    label="NO_AUTO_SHIFT_NUMERIC"
                    checked={noAutoShiftNumeric}
                    onChange={setNoAutoShiftNumeric}
                    helperText={t('Disable auto shift for number keys (0-9)')}
                  />
                  <SwitchField
                    label="NO_AUTO_SHIFT_ALPHA"
                    checked={noAutoShiftAlpha}
                    onChange={setNoAutoShiftAlpha}
                    helperText={t('Disable auto shift for letter keys (A-Z)')}
                  />
                </Stack>
              </div>
            </Stack>
          )}

          {/* === One Shot Tab === */}
          {activeTab === 4 && (
            <Stack spacing={3}>
              <div>
                <SectionHeader>{t('One Shot')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="ONESHOT_TIMEOUT"
                    size="small"
                    type="number"
                    fullWidth
                    value={oneshotTimeout}
                    onChange={(e) => setOneshotTimeout(e.target.value)}
                    placeholder="5000"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time in ms before one-shot key expires (default: 5000)'
                    )}
                  />
                  <TextField
                    label="ONESHOT_TAP_TOGGLE"
                    size="small"
                    type="number"
                    fullWidth
                    value={oneshotTapToggle}
                    onChange={(e) => setOneshotTapToggle(e.target.value)}
                    placeholder="5"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Number of taps to toggle one-shot key (default: 5)'
                    )}
                  />
                </Stack>
              </div>
            </Stack>
          )}

          {/* === Leader Key Tab === */}
          {activeTab === 5 && (
            <Stack spacing={3}>
              <div>
                <SectionHeader>{t('Leader Key')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="LEADER_TIMEOUT"
                    size="small"
                    type="number"
                    fullWidth
                    value={leaderTimeout}
                    onChange={(e) => setLeaderTimeout(e.target.value)}
                    placeholder="300"
                    inputProps={{ min: 0 }}
                    helperText={t(
                      'Time in ms for leader key sequence (default: 300)'
                    )}
                  />
                  <SwitchField
                    label="LEADER_PER_KEY_TIMING"
                    checked={leaderPerKeyTiming}
                    onChange={setLeaderPerKeyTiming}
                    helperText={t(
                      'Reset timeout after each key in leader sequence'
                    )}
                  />
                  <SwitchField
                    label="LEADER_NO_TIMEOUT"
                    checked={leaderNoTimeout}
                    onChange={setLeaderNoTimeout}
                    helperText={t(
                      'Disable leader key timeout (wait indefinitely for sequence)'
                    )}
                  />
                </Stack>
              </div>
            </Stack>
          )}

          {/* === Hardware Tab === */}
          {activeTab === 6 && (
            <Stack spacing={3}>
              <div>
                <SectionHeader>{t('Hardware')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="USB_VBUS_PIN"
                    size="small"
                    fullWidth
                    value={usbVbusPin}
                    onChange={(e) => setUsbVbusPin(e.target.value)}
                    placeholder="GP19"
                    helperText={t(
                      'GPIO pin to detect USB VBUS for split keyboard host detection'
                    )}
                  />
                </Stack>
              </div>
            </Stack>
          )}

          {/* === Audio Tab === */}
          {activeTab === 7 && (
            <Stack spacing={3}>
              <div>
                <SectionHeader>{t('Audio')}</SectionHeader>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="AUDIO_PIN"
                    size="small"
                    fullWidth
                    value={audioPin}
                    onChange={(e) => setAudioPin(e.target.value)}
                    placeholder="C6"
                    helperText={t('Primary audio output pin (e.g. C6, B5)')}
                  />
                  <TextField
                    label="AUDIO_PIN_ALT"
                    size="small"
                    fullWidth
                    value={audioPinAlt}
                    onChange={(e) => setAudioPinAlt(e.target.value)}
                    placeholder="B5"
                    helperText={t(
                      'Secondary audio output pin for stereo or alternative output'
                    )}
                  />
                  <SwitchField
                    label="AUDIO_VOICES"
                    checked={audioVoices}
                    onChange={setAudioVoices}
                    helperText={t(
                      'Enable multiple audio voices to play different sounds simultaneously'
                    )}
                  />
                  <SwitchField
                    label="AUDIO_INIT_DELAY"
                    checked={audioInitDelay}
                    onChange={setAudioInitDelay}
                    helperText={t(
                      'Add a delay during audio initialization to avoid startup noise'
                    )}
                  />
                </Stack>
              </div>
            </Stack>
          )}
        </div>
      </div>
    </div>
  );
}
